import PropTypes from 'prop-types';
import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {OBJECTS_CREATABLE_PERMISSION} from 'auth/PermissionConstants';
import * as benchlingUtil from 'BenchlingUtil';
import {TagSchemasSSIQuery} from 'bulk-import/panes/__generated/TagSchemasSSIQuery';
import {TAG_SCHEMAS_SSI_QUERY} from 'bulk-import/panes/EntitySchemaImplementingSSISelectionWithRestrictions';
import {BulkImportOptions, SelectedDestination} from 'bulk-import/SpreadsheetBulkImportStore';
import {
  getRegistrySupportedOligoAwareTypes,
  SchemalessBulkImportSupportedOligoAwareTypes,
} from 'constants/RegistryConstants';
import {SCHEMALESS_BULK_IMPORT_SUPPORTED_OLIGO_AWARE_TYPES} from 'constants/RegistryConstants';
import FolderSelector from 'file-browser/FolderSelector';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import HelpButton from 'help/HelpButton';
import {isComplexPolymerSchema} from 'modbio/complex-polymers/ComplexPolymerSchemaUtil';
import {ComplexPolymerSchemaConfigGraphQL} from 'modbio/complex-polymers/ComplexPolymerUtil';
import NavStore from 'nav/NavStore';
import * as RegistryUtil from 'registry/RegistryUtil';
import {TRIAL_SAMPLE_SCHEMA_INTERFACE_ID} from 'registry/trial-interfaces/TrialInterfaceConstants';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

import ModalButtonKey from '../../test/lib/cypress/shared/ModalButtonKey';
import * as SpreadsheetBulkImportActions from '../SpreadsheetBulkImportActions';
import {OPTIONS, SelectedSchemaIds, SpreadsheetPropTypes} from '../SpreadsheetBulkImportConstants';
import BioentityImportOptions from './BioentityImportOptions';

const COMPLEX_POLYMER_SCHEMA_CONFIGURATION_SHAPE = PropTypes.shape({
  id: PropTypes.string,
  schema: PropTypes.shape({
    id: PropTypes.string,
  }),
  linkedFields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ),
});

export interface ChooseImportLocationModalPaneProps {
  selectedDestination: SelectedDestination | null;
  getEnabledOptions?: ({
    options,
    selectedSchemaIds,
  }: {
    options?: BulkImportOptions;
    selectedSchemaIds: SelectedSchemaIds;
  }) => Array<OPTIONS>;
  isLoading: boolean;
  modalProps: {
    title: string;
    onRequestClose: () => void;
  };
  onDestinationFolderChange?: (folderId: string | null) => void;
  options: BulkImportOptions;
  schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes | null;
  selectedSchemaIds: SelectedSchemaIds;
  showRegistries: boolean;
  showInventory: boolean;
  shouldShowSchemaSelector?: boolean;
  isSchemaRequired?: boolean;
  folderSelectorLabel?: string;
  folderSelectorPopoverContent?: string;
  complexPolymerSchemaConfigurations?: Array<ComplexPolymerSchemaConfigGraphQL>;
  // Whether to filter the schema selector to only show complex polymer schemas
  shouldFilterToComplexPolymerSchemas: boolean;
}

export type ChooseImportLocationModalPaneConfiguration = Omit<
  ChooseImportLocationModalPaneProps,
  | 'selectedDestination'
  | 'isLoading'
  | 'modalProps'
  | 'options'
  | 'selectedSchemaIds'
  | 'schemalessCreationEntityType'
>;

class ChooseImportLocationModalPane extends React.Component<ChooseImportLocationModalPaneProps> {
  static propTypes = {
    selectedDestination: PropTypes.shape({
      folderId: PropTypes.string.isRequired,
      registryId: PropTypes.string,
    }),
    getEnabledOptions: PropTypes.func.isRequired,
    // true if there's anything stopping us from proceeding to the next step. In practice this
    // happens when we are creating or updating samples and the batch schema hasn't been loaded yet.
    isLoading: PropTypes.bool.isRequired,
    modalProps: SpreadsheetPropTypes.modalProps,
    onDestinationFolderChange: PropTypes.func,
    options: PropTypes.objectOf(PropTypes.bool).isRequired,
    schemalessCreationEntityType: PropTypes.oneOf(SCHEMALESS_BULK_IMPORT_SUPPORTED_OLIGO_AWARE_TYPES),
    selectedSchemaIds: SpreadsheetPropTypes.selectedSchemaIds,
    showRegistries: PropTypes.bool.isRequired,
    showInventory: PropTypes.bool.isRequired,
    shouldShowSchemaSelector: PropTypes.bool.isRequired,
    isSchemaRequired: PropTypes.bool,
    complexPolymerSchemaConfigurations: PropTypes.arrayOf(COMPLEX_POLYMER_SCHEMA_CONFIGURATION_SHAPE),
  };

  /**
   * Returns configuration for ChooseImportLocationModalPane for a given import flow.
   * @param {showRegistries}     Boolean whether to include registries in folder selector.
   * @param {showInventory}      Boolean whether to include inventory folders in folder selector.
   * @param {getEnabledOptions}  Called with ({options, selectedSchemaIds}) and should return an array of
   *                             enabled options.
   * @param {shouldShowSchemaSelector} Boolean whether to show the schema selector
   * @param {isSchemaRequired} Boolean whether or not selecting a schema should be required
   */
  static configure({
    showRegistries,
    showInventory,
    onDestinationFolderChange,
    getEnabledOptions = () => [],
    shouldShowSchemaSelector = true,
    isSchemaRequired = true,
    folderSelectorLabel,
    folderSelectorPopoverContent,
    shouldFilterToComplexPolymerSchemas = false,
  }: ChooseImportLocationModalPaneConfiguration): ChooseImportLocationModalPaneConfiguration {
    assert(showRegistries != null, 'Must configure with `showRegistries`.');
    // TODO(CUST-674): change to simple assert once we are confident there are no missed callsites
    assertAndContinue(showInventory != null, 'Must configure with `showInventory`.');
    return {
      showRegistries,
      showInventory,
      getEnabledOptions,
      shouldShowSchemaSelector,
      onDestinationFolderChange,
      isSchemaRequired,
      folderSelectorLabel,
      folderSelectorPopoverContent,
      shouldFilterToComplexPolymerSchemas,
    };
  }

  static defaultProps = {
    isSchemaRequired: true,
  };

  _handleDestinationFolderChange = (e: {target: {value: string}}): void => {
    // If the user selects a registry, we'll get a special combined value that we need to extract.
    // parseFolderValue also handles that case where it's just a regular folderId.
    const {folderId, registryId} = RegistryUtil.parseFolderValue(e.target.value);
    SpreadsheetBulkImportActions.setImportSetting({selectedDestination: {folderId, registryId}});
    if (this.props.onDestinationFolderChange != null) {
      this.props.onDestinationFolderChange(folderId);
    }
  };

  _handleTagSchemaChange = (tagSchema: {id: string} | null): void => {
    if (tagSchema != null) {
      SpreadsheetBulkImportActions.setImportSetting({selectedTagSchemaId: tagSchema.id});
    } else if (!this.props.isSchemaRequired) {
      SpreadsheetBulkImportActions.setImportSetting({selectedTagSchemaId: null});
    } else {
      assert(false, 'TagSchemaSelector returned null for the tagSchema');
    }
  };

  _openNewSchemaPage = (): void => {
    const registry = NavStore.getRegistries()[0];
    this.props.modalProps.onRequestClose();
    benchlingUtil.navigate(
      null,
      RegistryUtil.getCreateTagSchemaUrl(registry.s_owner.url_identifier, registry.id)
    );
  };

  schemaPopoverContent = (): JSX.Element => (
    <div>
      <p>
        Schemas let you define new entity types. You can edit existing schemas and add new ones in your
        registry settings.
      </p>
    </div>
  );

  override render(): JSX.Element {
    const canProceed =
      this.props.selectedDestination &&
      (!this.props.shouldShowSchemaSelector ||
        this.props.selectedSchemaIds.tagSchemaId ||
        !this.props.isSchemaRequired) &&
      (!this.props.options[OPTIONS.IMPORT_CONTAINERS] || this.props.selectedSchemaIds.containerSchemaId) &&
      !this.props.isLoading;
    let folderId;
    let registryId: string | null = null;

    if (this.props.selectedDestination) {
      assert(Boolean(this.props.selectedDestination.folderId), 'Selected destination is missing folderId.');
      registryId = this.props.selectedDestination.registryId;
      folderId = this.props.selectedDestination.folderId;
    }

    const folderSelectorId = registryId
      ? // @ts-expect-error We assert above instead of enforcing folder id be non-null in onDestinationFolderChange
        RegistryUtil.generateFolderValue(folderId, registryId, '')
      : folderId;
    const hasRegistrySettingsAccess =
      NavStore.getRegistries().length &&
      NavStore.getRegistries().some((registry) => RegistryUtil.hasSettingsAccess(registry));
    return (
      <Modal
        enableRedesign={false}
        buttons={[
          {
            key: 'prev',
            text: 'Next',
            style: ButtonStyle.SECONDARY,
            onClick: SpreadsheetBulkImportActions.goToNextPane,
            disabled: !canProceed,
            dataTestAttrs: {
              component: 'ImportLocationModal',
              element: 'footerButton',
              key: ModalButtonKey.NEXT,
            },
          },
        ]}
        {...this.props.modalProps}
      >
        <Row>
          <Col xs={12}>
            <div className="form-group">
              <span>
                <FieldLabel label={this.props.folderSelectorLabel} isRequired={true} />
                {this.props.folderSelectorPopoverContent && (
                  <HelpButton
                    renderContent={() => this.props.folderSelectorPopoverContent}
                    iconClassName="bulkImportModal-helpIcon u-tiny-spacing-left"
                  />
                )}
              </span>
              <FolderSelector
                enableRedesign={true}
                onChange={this._handleDestinationFolderChange}
                showRegistries={this.props.showRegistries}
                showInventory={this.props.showInventory}
                value={folderSelectorId}
                unsetOnFetchFail={true}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {this.props.shouldShowSchemaSelector && (
              <div>
                <div className="form-group">
                  <span>
                    <FieldLabel label="Schema" isRequired={this.props.isSchemaRequired} />
                    <HelpButton
                      renderContent={this.schemaPopoverContent}
                      iconClassName="bulkImportModal-helpIcon u-tiny-spacing-left"
                    />
                  </span>
                  <BenchlingQuery<TagSchemasSSIQuery>
                    query={TAG_SCHEMAS_SSI_QUERY}
                    renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
                  >
                    {(data) => {
                      const interfaceIdsBySchemaId = Object.fromEntries(
                        data.tagSchemas.map((schema) => [
                          schema.id,
                          schema.interfaceConfigs.map((interfaceConfig) => interfaceConfig.interface.id),
                        ])
                      );

                      const tagSchemaFilterFn = (schema: {id: string}): boolean => {
                        if (interfaceIdsBySchemaId[schema.id]?.includes(TRIAL_SAMPLE_SCHEMA_INTERFACE_ID)) {
                          return false;
                        }

                        if (
                          this.props.complexPolymerSchemaConfigurations != null &&
                          this.props.shouldFilterToComplexPolymerSchemas
                        ) {
                          return isComplexPolymerSchema(
                            this.props.complexPolymerSchemaConfigurations,
                            schema.id
                          );
                        }

                        return true;
                      };

                      return (
                        <TagSchemaSelector
                          oligoAwareFolderItemTypes={
                            this.props.schemalessCreationEntityType
                              ? [this.props.schemalessCreationEntityType]
                              : getRegistrySupportedOligoAwareTypes()
                          }
                          onChange={this._handleTagSchemaChange}
                          registryId={registryId}
                          requiredPermissions={[OBJECTS_CREATABLE_PERMISSION]}
                          selectedTagSchemaId={this.props.selectedSchemaIds.tagSchemaId}
                          allowNone={!this.props.isSchemaRequired}
                          tagSchemaFilterFn={tagSchemaFilterFn}
                        />
                      );
                    }}
                  </BenchlingQuery>
                </div>
                {hasRegistrySettingsAccess && (
                  <div className="u-spacing-bottom">
                    Don't see the schema you're looking for?&nbsp;
                    <a onClick={this._openNewSchemaPage}>Create a new one</a>.
                  </div>
                )}
              </div>
            )}
            <BioentityImportOptions
              options={this.props.options}
              enabledOptions={this.props.getEnabledOptions!(this.props)}
              selectedSchemaIds={this.props.selectedSchemaIds}
            />
          </Col>
        </Row>
      </Modal>
    );
  }
}

export default ChooseImportLocationModalPane;
